import Titulo from '@/components/tipografia/Titulo';
import React, { useCallback } from 'react';
import styles from './page.module.css';
import LayoutUsuario from '@/components/navegacao/LayoutUsuario';
import Descricao from '@/components/tipografia/Descricao';
import HBox from '@/components/layout/HBox';
import Botao from '@/components/Botao';
import VBox from '@/components/layout/VBox';
import CampoTexto from '@/components/Formulario/CampoTexto';
import Icon from '@mdi/react';
import { mdiAccountGroup, mdiAlertOutline, mdiKeyPlus, mdiLoading, mdiMagnify, mdiPencil, mdiPlus, mdiTrashCan } from '@mdi/js';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import { useInfiniteQuery, useMutation } from 'react-query';
import axios from 'axios';
import BotaoPerigoso from '@/components/BotaoPerigoso';
import { useDebounce } from '@/lib/useDebounce';
import { toastSuccess, toastWarning } from '@/lib/toast';
import { extractApiError } from '@/lib/apiError';
import Modal from '@/components/layout/Modal';
import AlertBox from '@/components/layout/AlertBox';
import CodeBlock from '@/components/layout/CodeBlock';
import { FormUsuario } from './FormUsuario';
import { useQueryClient } from 'react-query';
import { GruposManagerModal } from '@/usuario_grupo/GruposManagerModal';
import { getInstallBaseUrl } from '@/lib/tenant';

type ResponseList = {
  status: number;
  ok?: boolean;
  result: any[];
  total: number;
  cursor: string | number | null;
};

type usuarioPageProps = {};

const usuarioPage = ({ }: usuarioPageProps) => {
  const queryClient = useQueryClient();
  const [showGruposModal, setShowGruposModal] = React.useState(false);
  const [editObject, setEditObject] = React.useState<any>(null);
  const [fieldErrors, setFieldErrors] = React.useState<Record<
    string,
    string
  > | null>(null);
  const [linkGerado, setLinkGerado] = React.useState<{ token: string; expira_em: string } | null>(null);

  const [search, setSearch] = React.useState<string>('');
  const debouncedSearch = useDebounce(search, 500);

  const headers: DataTableHeader[] = [
    { label: 'Nome', width: '50%', column: 'nome' },
    {
      label: 'Grupo / Departamento',
      render: (row: any): string => row.grupo?.nome ?? '',
    },
    {
      label: 'Ações',
      width: '100px',
      stickRight: true,
      alignRight: true,
      render: (row: any) => {
        return (
          <HBox gap="8px" style={{ justifyContent: 'flex-end' }}>
            <Botao
              variant="none-primary"
              icon={mdiKeyPlus}
              title="Gerar link de acesso único"
              onClick={() => acessoTokenMutator.mutate(row.id)}
            />
            <Botao
              variant="none-primary"
              icon={mdiPencil}
              onClick={() => setEditObject(row)}
            />
            <BotaoPerigoso
              variant="none-danger"
              icon={mdiTrashCan}
              onClick={() => handleDelete(row.id)}
            />
          </HBox>
        );
      },
    },
  ];

  const queryList = useInfiniteQuery({
    queryKey: ['usuario', debouncedSearch],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({
        txt: debouncedSearch,
        c: pageParam,
      });

      return await axios.get<ResponseList>(
        `/api/usuario?${params.toString()}`,
        {
          withCredentials: true,
        },
      );
    },
    getNextPageParam: lastPage => lastPage.data.cursor ?? null,
  });

  const changeMutator = useMutation(
    async () => {
      if (!editObject) return;

      setFieldErrors(null);
      if (editObject.id) {
        return await axios.put(`/api/usuario/${editObject.id}`, editObject, {
          withCredentials: true,
        });
      } else {
        return await axios.post(`/api/usuario`, editObject, {
          withCredentials: true,
        });
      }
    },
    {
      onSuccess: () => {
        setFieldErrors(null);
        queryList.refetch();
        setEditObject(null);
      },
      onError: (err: unknown) => {
        const { message, fieldErrors: errs } = extractApiError(err, 'Erro desconhecido');
        if (errs) setFieldErrors(errs);
        toastWarning(message);
      },
    },
  );

  const deleteMutator = useMutation(
    async (id: number) => {
      return await axios.delete(`/api/usuario/${id}`, {
        withCredentials: true,
      });
    },
    {
      onSuccess: () => {
        toastSuccess('Usuário apagado com sucesso');
        queryList.refetch();
      },
      onError: (err: unknown) => {
        const { message } = extractApiError(err, 'Ocorreu um erro ao tentar apagar o usuário.');
        toastWarning(message);
      },
      onSettled: () => {
        queryList.refetch();
      },
    },
  );

  const acessoTokenMutator = useMutation(
    async (id: number) => {
      const res = await axios.post(`/api/usuario/${id}/acesso_token`, {}, {
        withCredentials: true,
      });
      return res.data;
    },
    {
      onSuccess: data => {
        setLinkGerado({ token: data.token, expira_em: data.expira_em });
        // O backend marcou "alterar senha no próximo login" pra este usuário —
        // atualiza a lista pra quem reabrir o cadastro ver o checkbox refletido.
        queryList.refetch();
      },
      onError: (err: unknown) => {
        const { message } = extractApiError(err, 'Erro ao gerar o link de acesso.');
        toastWarning(message);
      },
    },
  );

  const handleChange = useCallback(async () => {
    await changeMutator.mutate();
  }, [changeMutator]);

  const handleDelete = (id: number) => {
    deleteMutator.mutate(id);
  };

  const handleCloseModal = () => {
    setEditObject(null);
    setFieldErrors(null);
  };

  const handleOpenModalNewObject = () => {
    setEditObject({});
  };

  const queryFlatList =
    queryList.data?.pages?.map(p => p.data.result)?.flat() ?? [];
  const total =
    queryList.data?.pages?.[queryList.data.pages.length - 1]?.data?.total ?? 0;
  const editMode = !!editObject?.id && editObject.id > 0;

  return (
    <LayoutUsuario>
      {showGruposModal && (
        <GruposManagerModal
          onClose={() => {
            setShowGruposModal(false);
            queryClient.invalidateQueries(['grupos']);
          }}
        />
      )}
      <Modal
        ignoreOverlayClick
        title={editMode ? 'Editar usuário' : 'Novo usuário'}
        show={!!editObject}
        onClose={handleCloseModal}
        onCancel={handleCloseModal}
        onConfirm={handleChange}
      >
        <FormUsuario
          value={editObject as any}
          onChange={setEditObject}
          fieldErrors={fieldErrors}
        />
      </Modal>
      <Modal
        instanceId="modal-usuario-acesso-token-gerado"
        size="md"
        title="Link de acesso único gerado"
        show={!!linkGerado}
        closeable={false}
        ignoreOverlayClick
        onClose={() => setLinkGerado(null)}
        onCancel={() => setLinkGerado(null)}
        cancelProps={{ label: 'Já copiei, fechar' }}
        confirmProps={{ visible: false }}
      >
        {linkGerado && (
          <VBox style={{ gap: '12px' }}>
            <AlertBox variant="warning">
              <HBox style={{ gap: '8px', alignItems: 'flex-start' }}>
                <Icon path={mdiAlertOutline} size="20px" />
                <span>Notas:
                  <ul>
                    <li>Este link será mostrado apenas uma vez.</li>
                    <li>Gerar um novo link invalida o anterior deste usuário.</li>
                    <li>Ao entrar, as outras sessões abertas do usuário são encerradas.</li>
                    <li>Ao entrar, o usuário será obrigado a definir uma nova senha antes de continuar.</li>
                    <li>Link válido por 24 horas.</li>
                  </ul>
                </span>
              </HBox>
            </AlertBox>

            <VBox style={{ gap: '6px' }}>
              <span style={{ fontSize: '13px', fontWeight: 600 }}>Link de acesso</span>
              <CodeBlock value={`${getInstallBaseUrl()}/login/token#${linkGerado.token}`} />
            </VBox>

            <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-600)' }}>
              Expira em {new Date(linkGerado.expira_em).toLocaleString()}.
            </span>
          </VBox>
        )}
      </Modal>
      <VBox gap="16px" style={{ height: 'calc(100vh - 32px)' }}>
        {/* Header */}
        <HBox>
          <VBox>
            <Titulo>Usuários</Titulo>
            <Descricao>Gerencie seus usuários aqui</Descricao>
          </VBox>
          <HBox gap="12px" className={styles.headerAcoes}>
            <Botao
              variant="outline-secondary"
              icon={mdiAccountGroup}
              onClick={() => setShowGruposModal(true)}
            >
              Gerenciar grupos
            </Botao>
            <Botao
              variant="primary"
              icon={mdiPlus}
              onClick={handleOpenModalNewObject}
            >
              Novo
            </Botao>
          </HBox>
        </HBox>

        {/* Filtros */}
        <HBox>
          <CampoTexto
            prepend={
              <Icon
                path={mdiMagnify}
                size={1}
                color="var(--tc-color-gray-500)"
              />
            }
            placeholder="Pesquisar"
            value={search}
            onChange={setSearch}
          />
        </HBox>

        {queryList.isFetching && (
          <div>
            Aguarde...{' '}
            <Icon
              path={mdiLoading}
              size="14px"
              color="var(--tc-color-gray-700)"
              spin
            />
          </div>
        )}
        {!queryList.isFetching && (
          <div>
            Exibindo {queryFlatList?.length ?? '0'} de {total ?? '0'}{' '}
            resultados.
          </div>
        )}

        <DataTable
          isLoading={queryList.isFetching}
          cabecalhos={headers}
          linhas={queryFlatList ?? []}
          hasMoreData={queryList.hasNextPage}
          onLoadMore={async () => queryList.fetchNextPage()}
        />
      </VBox>
    </LayoutUsuario>
  );
};

export default usuarioPage;
